import React from 'react';
import { DeleteDialog, ErpDataTableV2 } from '@/components/custom/erp';
import { KheUoc, KheUocInput } from '@/types/schemas';
import { ActionBar, FormDialog } from './components';
import { Columns } from './cols-definition';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function KheUocPage() {
  const {
    data: kheUocs,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem: addKheUoc,
    updateItem: updateKheUoc,
    deleteItem: deleteKheUoc,
    refreshData: refreshKheUocs,
    searchData
  } = useCRUD<KheUoc, KheUocInput>({
    endpoint: QUERY_KEYS.KHE_UOC,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<KheUoc>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : kheUocs || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: Columns
    }
  ];

  const handleFormSubmit = async (data: KheUocInput) => {
    try {
      if (formMode === 'add') {
        await addKheUoc({ ...data, action: 'NEW' });
      } else if (formMode === 'edit' && selectedObj) {
        await updateKheUoc(selectedObj.uuid, { ...data, action: 'EDIT' });
      }
      refreshKheUocs();
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <div className={`${showForm ? 'hidden' : 'block'}`}>
        <ActionBar
          onAddClick={handleAddClick}
          onEditClick={() => selectedObj && handleEditClick()}
          onDeleteClick={() => selectedObj && handleDeleteClick()}
          onCopyClick={() => selectedObj && handleCopyClick()}
          onViewClick={() => selectedObj && handleViewClick()}
          onRefreshClick={refreshKheUocs}
          isEditDisabled={!selectedObj}
          isViewDisabled={!selectedObj}
          isDeleteDisabled={!selectedObj}
          isCopyDisabled={!selectedObj}
        />

        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          serverSidePagination={true}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
        />
      </div>

      {showForm && (
        <FormDialog
          open={showForm}
          onSubmit={handleFormSubmit}
          formMode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onClose={handleCloseForm}
          onAdd={() => {
            handleCloseForm();
            clearSelection();
            setTimeout(() => {
              handleAddClick();
            }, 100);
          }}
          onEdit={() => {
            if (selectedObj) {
              handleCloseForm();
              handleEditClick();
            }
          }}
          onDelete={() => {
            if (selectedObj) {
              handleCloseForm();
              handleDeleteClick();
            }
          }}
          onCopy={() => {
            if (selectedObj) {
              handleCloseForm();
              handleCopyClick();
            }
          }}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deleteObj={deleteKheUoc}
          clearSelection={clearSelection}
        />
      )}
    </div>
  );
}
